import OpenAI from 'openai';
import api from './api';

// Importação com require para evitar problemas de tipo
const traducaoData = require('../data/traducao.json')

interface TraducaoExemplo {
  portugues: string
  glosa: string
}

// Configuração do cliente OpenAI
const client = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY || '',
  dangerouslyAllowBrowser: true, // Necessário para uso no browser
})

const MODELO_OPENAI = 'gpt-5.4-nano'
const MODELO_EMBEDDING = 'text-embedding-3-small'

interface ChatGPTResponse {
  resposta_pt: string
  glosa: string[]
}

/**
 * Interface para representar uma mensagem no histórico
 */
export interface ConversationMessage {
  role: 'user' | 'assistant'
  content: string
}

/**
 * Constrói o prompt do sistema com exemplos few-shot
 */
function construirSystemPrompt(): string {
  const regras = `
Você é o motor de tradução do projeto LISA, especializado em converter Português para GLOSA de LIBRAS.

[REGRAS RÍGIDAS]
1. A estrutura deve seguir a gramática da LIBRAS (ex: Tópico-Comentário).
2. Gere as respostas usando um português simplificado e acessível
3. Padrão das respostas S-V-O, Sujeito, Verbo e Objeto.
4. Se perguntarem, seu nome é Lisa e você é uma assistente virtual de LIBRAS que trabalha na empresa Dell.
5. As glosas possuem um padrão S-O-V, Sujeito, Objeto e Verbo.
6. Cada elemento do array 'glosa' deve ser UMA ÚNICA PALAVRA. Você pode combinar palavras em um mesmo elemento, mas não coloque duas palavras em um mesmo elemento, veja os exemplos:
7. Não inclua URLs, hashtags, emojis ou qualquer outro elemento que não seja texto.
`

  // Exemplos few-shot dos primeiros 30 exemplos do arquivo de tradução
  const exemplos: TraducaoExemplo[] = traducaoData.slice(0, 30)
  const contexto = exemplos
    .map(
      (ex: TraducaoExemplo) =>
        `Português: ${ex.portugues} >>> GLOSA: ${ex.glosa}`,
    )
    .join('\n')

  const saida = `
Responda APENAS com um JSON estruturado:
{
  "resposta_pt": "texto em português simplificado",
  "glosa": ["PALAVRA1", "PALAVRA2", "PALAVRA3"]
}
`

  return regras + '\n\n' + contexto + '\n\n' + saida
}

/**
 * Envia mensagem para o ChatGPT e recebe resposta em GLOSA, mantendo histórico de conversa
 */
export async function chatGPT(
  userMessage: string,
  conversationHistory: ConversationMessage[] = [],
): Promise<ChatGPTResponse> {
  try {
    if (!process.env.REACT_APP_OPENAI_API_KEY) {
      throw new Error('API Key OpenAI não configurada')
    }

    const systemPrompt = construirSystemPrompt()

    // Monta o array de mensagens
    const inputMessages: any[] = [
      {
        role: 'system',
        content: systemPrompt,
      },
    ]

    // Adiciona o histórico de conversa
    for (const msg of conversationHistory) {
      inputMessages.push({
        role: msg.role,
        content: msg.content,
      })
    }

    // Adiciona a nova mensagem do usuário
    inputMessages.push({
      role: 'user',
      content: userMessage,
    })

    const response = await client.responses.create({
      model: MODELO_OPENAI,
      tools: [
        {
          type: 'web_search',
          search_context_size: 'medium',
        },
      ],
      tool_choice: 'auto',
      input: inputMessages,
      temperature: 0.7,
      max_output_tokens: 500,
    })

    const gptResponse = response.output_text || ''

    // Tenta fazer parse do JSON
    try {
      const parsed: ChatGPTResponse = JSON.parse(gptResponse)

      // Garante que cada entrada da glosa é uma única palavra
      if (parsed.glosa) {
        const expanded: string[] = []
        for (const item of parsed.glosa) {
          expanded.push(...item.split(/\s+/))
        }
        parsed.glosa = expanded
      }

      return parsed
    } catch (jsonError) {
      // Tenta extrair JSON do texto com regex
      const jsonMatch = gptResponse.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const parsed: ChatGPTResponse = JSON.parse(jsonMatch[0])

        // Garante que cada entrada da glosa é uma única palavra
        if (parsed.glosa) {
          const expanded: string[] = []
          for (const item of parsed.glosa) {
            expanded.push(...item.split(/\s+/))
          }
          parsed.glosa = expanded
        }

        return parsed
      }
      throw new Error('Resposta não está em formato JSON válido')
    }
  } catch (error) {
    console.error('Erro ao chamar ChatGPT:', error)
    throw error
  }
}

/**
 * Gera embeddings para uma lista de palavras
 */
export async function generateEmbeddings(words: string[]): Promise<number[][]> {
  try {
    if (!process.env.REACT_APP_OPENAI_API_KEY) {
      throw new Error('API Key OpenAI não configurada')
    }

    const MAX_BATCH_SIZE = 32
    const allVectors: number[][] = []

    for (let i = 0; i < words.length; i += MAX_BATCH_SIZE) {
      const chunk = words.slice(i, i + MAX_BATCH_SIZE)
      const response = await client.embeddings.create({
        model: MODELO_EMBEDDING,
        input: chunk,
      })
      allVectors.push(...response.data.map((item) => item.embedding))
    }

    return allVectors
  } catch (error) {
    console.error('Erro ao gerar embeddings:', error)
    throw error
  }
}

/**
 * Gera embedding para uma única palavra
 */
export async function generateSingleEmbedding(word: string): Promise<number[]> {
  try {
    const embedding = await api
      .post("/ai/embeddings", {
        input: [word],
        dimensions: 1024,
      })
      .then((response) => response.data[0])
      .then((data: any) => {
        return data.embedding;
      });

    return embedding;

  } catch (error) {
    console.error('Erro ao gerar embedding:', error)
    throw error
  }
}
